import React, { useState } from 'react';
import axios from 'axios';
import { FileText, Download, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { API, toast } from '@/App';
import { getAuthToken } from '@/utils/authSession';

const ReportsExports = () => {
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('monthly');
  const [format, setFormat] = useState('pdf');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [useCustomDates, setUseCustomDates] = useState(false);

  const downloadReport = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const params = new URLSearchParams({
        period,
        format
      });
      
      if (useCustomDates && startDate && endDate) {
        params.append('start_date', new Date(startDate).toISOString());
        params.append('end_date', new Date(endDate).toISOString());
      }

      const response = await axios.get(`${API}/admin/reports/statistics?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rapport_${period}_${Date.now()}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Rapport téléchargé avec succès');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du rapport');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Rapports & Exports</h2>
        <p className="text-gray-600 mt-1">
          Générez et téléchargez vos rapports d'activité
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Statistics Reports */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-600" />
              Rapports Statistiques
            </CardTitle>
            <CardDescription>
              Bilans mensuel, trimestriel ou annuel
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Période</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensuel (mois en cours)</SelectItem>
                  <SelectItem value="quarterly">Trimestriel (trimestre en cours)</SelectItem>
                  <SelectItem value="annual">Annuel (année en cours)</SelectItem>
                  <SelectItem value="custom">Personnalisé</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {period === 'custom' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date début</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setUseCustomDates(true);
                    }}
                  />
                </div>
                <div>
                  <Label>Date fin</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setUseCustomDates(true);
                    }}
                  />
                </div>
              </div>
            )}

            <div>
              <Label>Format</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF (Formaté)</SelectItem>
                  <SelectItem value="csv">CSV (Tableur)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={downloadReport}
              disabled={loading || (period === 'custom' && (!startDate || !endDate))}
              className="w-full bg-gradient-to-r from-red-500 to-cyan-500 hover:from-red-600 hover:to-cyan-600"
            >
              <Download className="w-4 h-4 mr-2" />
              {loading ? 'Génération...' : 'Télécharger le rapport'}
            </Button>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-cyan-600" />
              Informations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-semibold text-blue-900">Rapport PDF</p>
                <p className="text-xs text-blue-700 mt-1">
                  Contient les statistiques détaillées, graphiques et répartition par ville.
                  Idéal pour les présentations et archives.
                </p>
              </div>

              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm font-semibold text-green-900">Export CSV</p>
                <p className="text-xs text-green-700 mt-1">
                  Données brutes exportables vers Excel/Google Sheets.
                  Permet analyses personnalisées et tableaux croisés dynamiques.
                </p>
              </div>

              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-sm font-semibold text-amber-900">Données incluses</p>
                <ul className="text-xs text-amber-700 mt-1 space-y-1">
                  <li>• Nombre total de livraisons</li>
                  <li>• Encaissements finalisés (FCFA et USD)</li>
                  <li>• Répartition par statut</li>
                  <li>• Statistiques par ville</li>
                  <li>• Nouveaux utilisateurs</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export info */}
      <Card className="border-2 border-cyan-200 bg-cyan-50">
        <CardContent className="p-4">
          <p className="text-sm text-cyan-900">
            <strong>💡 Astuce:</strong> Pour un suivi régulier, téléchargez les rapports mensuels 
            en début de mois pour archiver les performances du mois précédent.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsExports;
